'use client';

import { MarkdownRenderer } from '#/app/(root)/renderers/markdown-renderer';
import { Button } from '#/components/ui/button';
import {
  useConfiguredBlockExplorer,
  useConfiguredChain,
  useConfiguredChainId,
} from '#/data/wagmi/configured';
import { ellipsizeString } from '#/lib/ellipsize-string';
import { cn } from '#/lib/utils';
import { useAppKit, useDisconnect } from '@reown/appkit/react';
import { Translation } from 'intl-lib';
import {
  CircleAlert,
  ExternalLink,
  Globe,
  Loader2,
  Unplug,
  Wallet,
} from 'lucide-react';
import Link from 'next/link';
import { ComponentProps } from 'react';
import { Address, getAddress } from 'viem';
import { useAccount, useBalance, useEnsName, useSwitchChain } from 'wagmi';
import { AnimatedNumber } from '../display/animated-number';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { CopyToClipboardButton } from '../v2/copy-to-clipboard';
import { AccountAvatar } from './account-avatar';
import { AccountHoverCard } from './account-hover-card';

export function useIsAddressMatch(expectedAddress?: Address | null) {
  const { address } = useAccount();
  return (
    expectedAddress == null ||
    (address != null && getAddress(address) == getAddress(expectedAddress))
  );
}

export function useIsNetworkMatch() {
  const expectedChainId = useConfiguredChainId();
  const { chainId } = useAccount();
  return chainId === expectedChainId;
}

/**
 * Custom React hook to check if the wallet is connected, the address matches the expected address,
 * and the network matches the configured chain.
 *
 * @param expectedAddress - The expected wallet address to match against.
 * @returns `true` if the wallet is connected, the address matches, and the network matches; otherwise `false`.
 */
export function useIsWalletMatch(expectedAddress?: Address | null) {
  const { isConnected } = useAccount();
  const isAddressMatch = useIsAddressMatch(expectedAddress);
  const isNetworkMatch = useIsNetworkMatch();
  return isConnected && isAddressMatch && isNetworkMatch;
}

export function WalletInput({
  address: expectedAddress,
}: {
  address?: Address | null;
}) {
  const expectedChain = useConfiguredChain();
  const blockExplorer = useConfiguredBlockExplorer();

  const { isConnected, address } = useAccount();

  const { data: ensName } = useEnsName({ chainId: expectedChain.id, address });
  const { data: balance } = useBalance({
    chainId: expectedChain.id,
    address,
  });

  const isAddressMatch = useIsAddressMatch(expectedAddress);
  const isNetworkMatch = useIsNetworkMatch();
  const isWalletMatch = useIsWalletMatch(expectedAddress);

  const { disconnect } = useDisconnect();
  const { switchChainAsync, isPending } = useSwitchChain();

  const modal = useAppKit();

  return (
    <>
      {isConnected ? (
        <div className="flex flex-col gap-4 border rounded-md p-4">
          <div className="flex gap-4 justify-between">
            <div className="flex gap-4">
              <AccountAvatar
                address={address}
                diameter={20}
                className="size-10 shrink-0"
              />
              <div className="flex-1 flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <p className="text-base break-all">
                    {ensName ?? ellipsizeString(address, 6)}
                  </p>
                  {isNetworkMatch && (
                    <Badge variant="secondary">{expectedChain.name}</Badge>
                  )}
                </div>
                <span className="text-sm text-muted-foreground">
                  {balance != null ? (
                    <AnimatedNumber
                      value={balance.value}
                      fractionDigits={5}
                      suffix={` ${balance.symbol}`}
                    />
                  ) : (
                    '-'
                  )}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="rounded-full"
                    onClick={() => disconnect()}
                  >
                    <Unplug />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Disconnect</TooltipContent>
              </Tooltip>
            </div>
          </div>
          <div className="flex flex-col items-stretch sm:flex-row sm:items-center gap-2">
            <CopyToClipboardButton text={address} variant="outline" size="sm">
              <Translation
                path="EthereumStaking.AccountHoverCard.CopyAddressButtonLabel"
                params={[blockExplorer?.name]}
              />
            </CopyToClipboardButton>
            <Button asChild variant="outline" size="sm">
              <Link
                href={`${blockExplorer?.url}/address/${address}`}
                target="_blank"
                rel="noreferrer"
                prefetch={false}
              >
                <ExternalLink />
                <Translation
                  path="global.noun.view_on_button_label"
                  params={blockExplorer}
                />
              </Link>
            </Button>
          </div>
          {!isWalletMatch && (
            <div className="flex items-center gap-2">
              <Alert variant="destructive" className="bg-red-50 border-none">
                <CircleAlert />
                <AlertTitle>
                  {!isAddressMatch ? (
                    <Translation path="global.modules.wallet_input.address_alert.title" />
                  ) : !isNetworkMatch ? (
                    <Translation path="global.modules.wallet_input.network_alert.title" />
                  ) : null}
                </AlertTitle>
                <AlertDescription>
                  {!isAddressMatch ? (
                    <div>
                      <Translation
                        path="global.modules.wallet_input.address_alert.description"
                        renderer={MarkdownRenderer}
                        rendererProps={{
                          components: {
                            p: ({
                              className,
                              ...props
                            }: ComponentProps<'p'>) => (
                              <div
                                className={cn(
                                  'flex items-center flex-wrap gap-1',
                                  className
                                )}
                                {...props}
                              />
                            ),
                            AccountHoverCard,
                          },
                        }}
                        params={{ address: expectedAddress }}
                      />
                    </div>
                  ) : !isNetworkMatch ? (
                    <div className="flex flex-col gap-1">
                      <Translation
                        path="global.modules.wallet_input.network_alert.description"
                        renderer={MarkdownRenderer}
                        rendererProps={{
                          components: {
                            NetworkName: () => (
                              <span className="font-bold">
                                {expectedChain.name}{' '}
                                {expectedChain.testnet
                                  ? 'Test Network'
                                  : 'Mainnet'}
                              </span>
                            ),
                          },
                        }}
                      />
                      <div>
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          className="rounded-full"
                          onClick={() =>
                            switchChainAsync({
                              chainId: expectedChain.id,
                            })
                          }
                        >
                          {isPending ? (
                            <Loader2 className="animate-spin" />
                          ) : (
                            <Globe />
                          )}
                          <Translation path="global.modules.wallet_input.network_alert.action" />
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 border rounded-xl">
          <Button
            type="button"
            variant="primary"
            className="w-full"
            onClick={() => modal.open({ view: 'Connect' })}
          >
            <Wallet />
            Connect Wallet
          </Button>
        </div>
      )}
    </>
  );
}
